import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  AlertCircle,
  Car,
  CheckCircle2,
  Clock,
  DollarSign,
  Info,
  MessageSquare,
  RefreshCcw,
  TrendingUp,
  Users,
} from "lucide-react";
import { toast } from "sonner";

import { api, formatApiError } from "@/lib/api";
import { formatUSD } from "@/lib/format";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import {
  PageContainer,
  PageHeader,
  Skeleton,
  StatusPill,
  LEAD_STATUS,
  relativeTime,
} from "./dealerUi";

const KPITile = ({ label, value, sub, tone = "brand", testId, icon: Icon }) => {
  const tones = {
    brand: "bg-brand text-white",
    gold: "bg-[#F7EFD1] text-[#7A5C0E]",
    slate: "bg-white text-slate-900 border border-slate-200",
  };
  return (
    <div
      className={`rounded-2xl px-5 py-4 shadow-sm ${tones[tone]}`}
      data-testid={testId || "kpi-tile"}
    >
      <div className="flex items-center justify-between">
        <p className={`text-xs font-semibold uppercase tracking-widest ${tone === "brand" ? "text-white/70" : "text-slate-500"}`}>
          {label}
        </p>
        {Icon && (
          <Icon
            size={16}
            className={tone === "brand" ? "text-white/70" : "text-slate-400"}
          />
        )}
      </div>
      <p className="mt-2 font-display text-2xl sm:text-[28px] font-semibold tracking-tight">
        {value}
      </p>
      {sub && (
        <p className={`mt-1 text-[11px] ${tone === "brand" ? "text-white/70" : "text-slate-500"}`}>
          {sub}
        </p>
      )}
    </div>
  );
};

const eventIcon = (type) => {
  if (type.startsWith("lead")) return MessageSquare;
  if (type === "appt_outcome") return CheckCircle2;
  if (type === "inventory_import") return RefreshCcw;
  if (type === "vehicle_sold") return TrendingUp;
  return Activity;
};

const DealerDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: resp } = await api.get("/dealer/dashboard");
        if (alive) setData(resp);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (loading) {
    return (
      <PageContainer>
        <PageHeader title="Dashboard" subtitle="Loading your dealership overview…" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      </PageContainer>
    );
  }
  if (!data) {
    return (
      <PageContainer>
        <PageHeader title="Dashboard" />
        <p className="text-slate-500">No dashboard data available yet.</p>
      </PageContainer>
    );
  }

  const conversionPct = Math.round((data.conversion_rate || 0) * 100);
  const leadsByStatus = data.leads_by_status_counts || {};

  return (
    <PageContainer>
      <PageHeader
        title="Dashboard"
        subtitle="Your live inventory, lead, and revenue snapshot."
        testId="dealer-dashboard-header"
      />

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dashboard-kpis">
        <KPITile
          label="Active listings"
          value={data.active_listings}
          sub={`${data.sold_this_month} sold this month`}
          testId="kpi-active-listings"
          icon={Car}
          tone="slate"
        />
        <KPITile
          label="Total leads"
          value={data.total_leads}
          sub={`${data.new_leads_today} new today`}
          testId="kpi-total-leads"
          icon={Users}
          tone="slate"
        />
        <KPITile
          label="Appts this week"
          value={data.appts_this_week}
          sub={`${data.showed_up_this_month} showed up · ${data.no_show_this_month} no-show (mo.)`}
          testId="kpi-appts-week"
          icon={Clock}
          tone="slate"
        />
        <KPITile
          label="Show-up rate"
          value={`${conversionPct}%`}
          sub={`Avg ${data.avg_days_on_lot}d on lot`}
          testId="kpi-conversion"
          icon={TrendingUp}
          tone="slate"
        />
      </div>

      {/* Billing card */}
      <div
        className="mt-6 rounded-2xl overflow-hidden shadow-sm border border-slate-200 bg-white"
        data-testid="billing-card"
      >
        <div className="p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-slate-500 text-xs font-semibold uppercase tracking-widest">
              <DollarSign size={14} className="text-[#8B6914]" />
              Estimated billing this month
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      data-testid="billing-tooltip-trigger"
                      className="inline-flex items-center text-slate-400 hover:text-slate-600"
                      aria-label="Billing details"
                    >
                      <Info size={13} />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">
                    Billing is finalized at month end. Only &quot;Showed Up&quot; appointments count.
                    No monthly fees.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <p className="mt-2 font-display text-3xl sm:text-4xl font-semibold text-slate-900">
              {formatUSD(data.estimated_billing_this_month)}
            </p>
            <p className="mt-1 text-sm text-slate-500">
              <span className="font-semibold text-slate-800">{data.showed_up_this_month}</span> showed-up appts × $300
            </p>
          </div>
          <div className="rounded-xl bg-[#FCF3D9] border border-[#F1E1A8] px-4 py-3 text-sm text-[#7A5C0E] max-w-md">
            <p className="font-semibold">BeloMotors bills on outcomes, not subscriptions.</p>
            <p className="mt-1 text-[#7A5C0E]/80">
              You&apos;re only charged when a customer physically shows up for their appointment.
              Each show-up is $300 automatically charged to your card on file.
            </p>
          </div>
        </div>
      </div>

      {/* 2-column split — Phase 11 layout */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top vehicles */}
        <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-5" data-testid="top-vehicles-card">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-lg font-semibold text-slate-900">Top vehicles by lead activity</h3>
            <Button
              asChild
              variant="ghost"
              size="sm"
              className="text-brand"
              data-testid="top-vehicles-view-inventory"
            >
              <Link to="/dealer/inventory">View inventory →</Link>
            </Button>
          </div>
          {data.top_vehicles_by_leads?.length ? (
            <div className="mt-4 divide-y divide-slate-100">
              {data.top_vehicles_by_leads.map((v) => (
                <div key={v.id} className="flex items-center gap-3 py-3">
                  <div className="w-14 h-10 rounded-md overflow-hidden bg-slate-100 flex-shrink-0">
                    {v.images?.[0] && (
                      <img src={v.images[0]} alt="" className="w-full h-full object-cover" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">
                      {v.year} {v.make} {v.model} {v.trim || ""}
                    </p>
                    <p className="text-xs text-slate-500">
                      {formatUSD(v.price)} · {v.views_count} views
                    </p>
                  </div>
                  <StatusPill tone="blue">{v.lead_count} leads</StatusPill>
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-4 text-sm text-slate-500">No lead activity yet.</p>
          )}
        </div>

        {/* Phase 11 — Upcoming Appointments (NO customer PII exposed) */}
        <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="upcoming-appts-card">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-lg font-semibold text-slate-900">Upcoming appointments</h3>
            <Button asChild variant="ghost" size="sm" className="text-brand" data-testid="upcoming-appts-view-all">
              <Link to="/dealer/appointments">All →</Link>
            </Button>
          </div>
          <p className="text-[11px] text-slate-400 mt-0.5">
            Someone is coming to see these vehicles. Customer contact is handled by BeloMotors BDC.
          </p>
          <div className="mt-3 space-y-2">
            {(data.upcoming_appointments || []).length === 0 ? (
              <p className="text-sm text-slate-500 py-4 text-center">
                No scheduled or confirmed appointments.
              </p>
            ) : (
              (data.upcoming_appointments || []).map((a) => (
                <div key={a.id} className="rounded-lg border border-slate-100 px-3 py-2.5 hover:bg-slate-50" data-testid={`upcoming-appt-${a.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-slate-900">
                      {new Date(a.scheduled_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                    </p>
                    <StatusPill tone={a.status === "confirmed" ? "emerald" : "amber"}>
                      {a.status === "confirmed" ? "Confirmed" : "Scheduled"}
                    </StatusPill>
                  </div>
                  <p className="text-xs text-slate-600 mt-1">
                    {a.vehicle_summary || "Vehicle"}
                    {a.stock_number && <span className="ml-1 font-mono text-slate-400">· #{a.stock_number}</span>}
                  </p>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </PageContainer>
  );
};

export default DealerDashboard;
